import * as React from 'react';
import { useMemo, useCallback } from 'react';
import Modal from '../../../../layout/modal/Modal';
import ModalHeader from '../../../../layout/modal/ModalHeader';
import LocalDiv from '../../../../localization/LocalDiv';
import LocalBtn from '../../../../localization/LocalBtn';
import Spacer from '../../../../layout/Spacer';
import { FLEX_COLUMN, FLEX_ROW } from '../../../../layout/flexBox';
import { css } from '@emotion/core';
import { COLORS } from '../../../../globalStyle/colors';
import { btnStyle, BTN_SHIMMER } from '../../../../forms/styles/button';
import { Str } from '../../../../localization/translationKeys';
import Msg from '../../../../localization/Msg';
import { useProjIds } from '../../../../navigation/hooks';
import { useRemindRaters } from './hooks';
import { useHistory } from 'react-router-dom';


const styles = {
  root:               [
    FLEX_COLUMN,
    css`
      min-height: 300px;
      max-width: 565px;
    `,
  ],
  modalContent:       [
    FLEX_COLUMN,
    css`
      flex-grow: 1;
      justify-content: space-between;
    `,
  ],
  modalText:          [
    FLEX_COLUMN,
    css`
      margin: 65px 85px 85px;

      @media only screen and (max-width: 470px) {
        margin: 65px 20px 85px;
      }
    `,
  ],
  confirmationTxt:    {
    root:       css`
      display: block;
      font-size: 18px;
      color: ${COLORS.DARK_GREY};
      font-weight: bold;
      line-height: 23px;
      overflow-wrap: break-word;
    `,
    spacer:     css`
      padding: 0 5px;
    `,
    warningMsg: css`
      display: inline;
      color: ${COLORS.RED}
    `,
    question:   css`
      display: inline;
    `,
  },
  additionalNoteText: css`
    display: block;
    font-size: 12px;
    color: ${COLORS.DARK_GREY};
    overflow-wrap: break-word;
  `,
  btnRow:             [
    FLEX_ROW,
    css`
      justify-content: flex-end;
      margin: 0 20px 20px;
    `,
  ],
  secondaryBtn:       [
    btnStyle.transparent(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
  primaryBtn:         [
    btnStyle.primary(COLORS.BLUE),
    css`
      min-width: 135px;
    `,
  ],
};


type Props = {
  className?: string;
};


/**
 * RemindRaters: a component that renders a modal asking the participant to confirm that they wish to send reminders to
 * all raters currently associated with their survey.
 *
 * PureComponent: true
 * Redux Connected: false
 */
const RemindRatersModal: React.FC<Props> = props => {
  const history      = useHistory();
  const projIds      = useProjIds();
  const remindRaters = useRemindRaters(projIds.projId, projIds.participantId);

  const primaryMsgStr = useMemo(
    () => remindRaters.remindersSent
      ? Str.RemindersSent
      : remindRaters.autoRemindersOn
        ? Str.AutoRemindersConfirm
        : Str.ConfirmRemind,
    [remindRaters.remindersSent, remindRaters.autoRemindersOn],
  );

  const onDismiss = useCallback(() => history.goBack(), [history]);

  // If the user opts to send reminders, we don't want them to be able to close the modal before they receive
  // confirmation of success. Hence, we disable dismissing the modal except via the provided buttons.
  return (
    <Modal dismissable={false} css={styles.root} className={props.className}>
      <ModalHeader onDismiss={onDismiss} />
      <div css={styles.modalContent}>
        <LocalDiv css={styles.modalText}>
          <LocalDiv css={styles.confirmationTxt.root}>
            {
              !remindRaters.remindersSent && remindRaters.autoRemindersOn && (
                <>
                  <Msg str={Str.AutoRemindersWarn} css={styles.confirmationTxt.warningMsg} />
                  <span css={styles.confirmationTxt.spacer} />
                </>
              )
            }
            <Msg str={primaryMsgStr} />
          </LocalDiv>
          <Spacer sizePx={24} />
          <LocalDiv css={styles.additionalNoteText}>
            <Msg str={Str.NoteForReminders} />
          </LocalDiv>
        </LocalDiv>

        <LocalDiv css={styles.btnRow}>
          {
            remindRaters.remindersSent
              ? <LocalBtn onClick={onDismiss} str={Str.Done} css={styles.primaryBtn} />
              : (
                <>
                  <LocalBtn
                    onClick={() => {remindRaters.sendReminders()}}
                    str={Str.Yes}
                    css={[styles.secondaryBtn, remindRaters.sendingReminders && BTN_SHIMMER]}
                    disabled={remindRaters.sendingReminders 
                      || !remindRaters.raterInvited 
                      || remindRaters.remindersSent
                    }
                  />
                  <Spacer sizePx={10} />
                  <LocalBtn
                    onClick={onDismiss}
                    str={Str.No}
                    css={[styles.primaryBtn, remindRaters.sendingReminders && BTN_SHIMMER]}
                    disabled={remindRaters.sendingReminders || !remindRaters.raterInvited}
                  />
                </>
              )
          }
        </LocalDiv>
      </div>
    </Modal>
  );
};

export default RemindRatersModal;
